import { useState, useMemo } from 'react';

type RejectReasonId =
  | 'skills'
  | 'interview'
  | 'availability'
  | 'references'
  | 'other'
  | 'flag';

interface RejectOption {
  id: RejectReasonId;
  label: string;
  description?: string | null;
  showNote: boolean;
  requireNote: boolean;
  notePlaceholder?: string;
}

const REJECT_OPTIONS: ReadonlyArray<RejectOption> = [
  { id: 'skills', label: 'Missing Skills', description: null, showNote: false, requireNote: false },
  { id: 'interview', label: 'Poor Interview', description: null, showNote: false, requireNote: false },
  { id: 'availability', label: 'Availability', description: null, showNote: false, requireNote: false },
  {
    id: 'references',
    label: 'References/Background',
    description: null,
    showNote: false,
    requireNote: false,
  },
  {
    id: 'other',
    label: 'Other',
    description: null,
    showNote: true,
    requireNote: true,
    notePlaceholder: 'Add a short explanation...',
  },
  {
    id: 'flag',
    label: 'Flag!',
    description: 'Flag against any future hiring',
    showNote: true,
    requireNote: true,
    notePlaceholder: 'Why should we flag this applicant?',
  },
] as const;

interface RejectCandidateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string, notes?: string) => void;
  candidateName?: string;
}

export default function RejectCandidateModal({
  isOpen,
  onClose,
  onConfirm,
  candidateName
}: RejectCandidateModalProps) {
  const [selectedRejectOption, setSelectedRejectOption] = useState<RejectReasonId | null>(null);
  const [rejectNotes, setRejectNotes] = useState('');

  const selectedRejectOptionMeta = useMemo(
    () => (selectedRejectOption ? REJECT_OPTIONS.find((opt) => opt.id === selectedRejectOption) ?? null : null),
    [selectedRejectOption]
  );

  const shouldShowRejectNotes = selectedRejectOptionMeta?.showNote ?? false;
  const isRejectNotesRequired = selectedRejectOptionMeta?.requireNote ?? false;
  const canConfirmReject = !!selectedRejectOption && (!isRejectNotesRequired || rejectNotes.trim().length > 0);

  const handleSelectRejectOption = (optionId: RejectReasonId) => {
    setSelectedRejectOption(optionId);
    const option = REJECT_OPTIONS.find((opt) => opt.id === optionId);
    if (!option?.showNote) {
      setRejectNotes('');
    }
  };

  const handleRejectOptionDoubleClick = (optionId: RejectReasonId) => {
    const option = REJECT_OPTIONS.find((opt) => opt.id === optionId);
    if (!option) return;
    handleSelectRejectOption(optionId);
    const notesValue = option.showNote ? rejectNotes.trim() : '';
    if (option.requireNote && !notesValue) {
      return;
    }
    handleConfirm(option.label, notesValue);
  };

  const handleConfirm = (reason: string, notes?: string) => {
    onConfirm(reason, notes);
    // Reset state
    setSelectedRejectOption(null);
    setRejectNotes('');
  };

  const handleClose = () => {
    setSelectedRejectOption(null);
    setRejectNotes('');
    onClose();
  };

  const handleConfirmClick = () => {
    if (!selectedRejectOptionMeta || !canConfirmReject) return;
    const notesValue = selectedRejectOptionMeta.showNote ? rejectNotes.trim() : '';
    handleConfirm(selectedRejectOptionMeta.label, notesValue);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-end">
      <button
        type="button"
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
        aria-label="Dismiss reject modal"
      />
      <div className="relative w-full rounded-t-3xl bg-white px-6 pb-6 pt-4 space-y-5 shadow-2xl">
        <div className="mx-auto h-1.5 w-12 rounded-full bg-gray-200" />
        <div className="text-center">
          <p className="text-lg font-semibold text-gray-900">
            Reject {candidateName || 'Candidate'}
          </p>
        </div>
        <div className="space-y-3">
          {REJECT_OPTIONS.map((option) => {
            const isSelected = selectedRejectOption === option.id;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => handleSelectRejectOption(option.id)}
                onDoubleClick={() => handleRejectOptionDoubleClick(option.id)}
                className={`
                  w-full rounded-xl border px-4 py-3 text-base font-semibold text-left transition
                  ${
                    isSelected
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-gray-200 text-gray-800 hover:border-gray-400'
                  }
                `}
              >
                <div>
                  <p>{option.label}</p>
                  {option.description && (
                    <p className="text-xs font-normal text-gray-500 mt-0.5">{option.description}</p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
        {shouldShowRejectNotes && (
          <textarea
            className="w-full rounded-2xl border border-gray-200 p-3 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-red-200"
            rows={3}
            placeholder={selectedRejectOptionMeta?.notePlaceholder ?? 'Add a quick note...'}
            value={rejectNotes}
            onChange={(e) => setRejectNotes(e.target.value)}
          />
        )}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 h-12 rounded-xl border border-gray-300 text-gray-600 font-semibold"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirmClick}
            disabled={!canConfirmReject}
            className={`
              flex-1 h-12 rounded-xl font-semibold text-white
              ${canConfirmReject ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-300 cursor-not-allowed'}
            `}
          >
            Confirm Rejection
          </button>
        </div>
      </div>
    </div>
  );
}

